import {useOnMountEffect} from "@khanacademy/wonder-blocks-core";
import {GuideStateApi} from "@khan/guide-state";
import type {GuideState} from "@khan/guide-types";

/**
 * A hook that invokes the provided callback when the specified field in the
 * Guide state changes (detected via event listeners outside of React).
 *
 * This is especially useful when it's necessary to subscribe to a change in
 * Guide state but do not wish for the consumer component to re-render on
 * every change – for example when a component has a computed state that is
 * derived from a Guide state value.
 *
 * Note: One nuance is that provided callback is invoked prior to the state
 * change being propagated into React state. That is, the given callback is
 * called before useGuideState(key) resolves an updated value.
 */
export const useGuideStateChangeEffect = <K extends keyof GuideState>(
    stateKey: K,
    callback: (state: Readonly<GuideState>) => void,
): void => {
    useOnMountEffect(() =>
        GuideStateApi.on("changed", (changedKey) => {
            if (changedKey === stateKey) {
                callback(GuideStateApi.latest());
            }
        }),
    );
};
